import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const JAPAN_PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option) =>
    typeof option === "string" ? option : option?.name || "",
  trim: true,
});

export default function RequiredJapaneseCityDialog() {
  const contextTheme = useContext(AppThemeContext);

  const theme = contextTheme || {
    surfaceBg: "#FFFFFF",
    bottomBorder: "rgba(0,0,0,0.08)",
    railActive: "#5A35FF",
    railText: "rgba(60,40,120,0.85)",
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [prefecture, setPrefecture] = useState("");
  const [city, setCity] = useState("");
  const [usState, setUsState] = useState("");
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [citiesByPrefecture, setCitiesByPrefecture] = useState({});
  const [loadingCities, setLoadingCities] = useState(true);

  const [usStatesByName, setUsStatesByName] = useState({});
  const [loadingUsStates, setLoadingUsStates] = useState(true);

  const isJapanesePrefecture = useMemo(() => {
    return JAPAN_PREFECTURES.includes(prefecture);
  }, [prefecture]);

  const isAmerica = prefecture === "海外（アメリカ）";

  const usStateOptions = useMemo(() => {
    return Object.values(usStatesByName).flat();
  }, [usStatesByName]);

  const selectedUsState = useMemo(() => {
    return usStateOptions.find((s) => s.locationKey === `US:${usState}`) || null;
  }, [usStateOptions, usState]);

  const cityOptions = useMemo(() => {
    if (!isJapanesePrefecture) return [];
    return citiesByPrefecture[prefecture] || [];
  }, [isJapanesePrefecture, prefecture, citiesByPrefecture]);

  const selectedCity = useMemo(() => {
    return cityOptions.find((c) =>
      typeof c === "string" ? c === city : c?.name === city
    ) || null;
  }, [cityOptions, city]);

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const res = await fetch("/data/japanCities.json", {
          cache: "force-cache",
        });
        if (!res.ok) {
          throw new Error(`failed to load japanCities.json: ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) {
          setCitiesByPrefecture(json || {});
        }
      } catch (err) {
        console.error("load japanCities.json error:", err);
        if (!cancelled) {
          setCitiesByPrefecture({});
        }
      } finally {
        if (!cancelled) {
          setLoadingCities(false);
        }
      }
    };

    loadCities();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadUsStates = async () => {
      setLoadingUsStates(true);
      try {
        const res = await fetch("/data/usStates.json", {
          cache: "force-cache",
        });
        if (!res.ok) {
          throw new Error(`failed to load usStates.json: ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) {
          setUsStatesByName(json || {});
        }
      } catch (err) {
        console.error("load usStates.json error:", err);
        if (!cancelled) {
          setUsStatesByName({});
        }
      } finally {
        if (!cancelled) {
          setLoadingUsStates(false);
        }
      }
    };

    loadUsStates();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;

    const userDocRef = doc(db, "users", auth.currentUser.uid);

    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const data = docSnap.data() || {};
      const currentPrefecture = data.prefecture || "";
      const currentCity = data.city || "";
      const currentUsState = data.usState || "";

      setPrefecture(currentPrefecture);
      setCity(currentCity);
      setUsState(currentUsState);

      const hasBasicInfo =
        String(data.registrationName || "").trim() &&
        String(data.registrationFurigana || "").trim() &&
        String(data.registrationEmail || "").trim() &&
        String(data.registrationPhone || "").trim();

      const shouldOpenJapan =
        JAPAN_PREFECTURES.includes(currentPrefecture) &&
        !String(currentCity).trim() &&
        hasBasicInfo;

      const shouldOpenUS =
        currentPrefecture === "海外（アメリカ）" &&
        !String(currentUsState).trim() &&
        hasBasicInfo;

      setOpenDialog(Boolean(shouldOpenJapan || shouldOpenUS));
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    if (isAmerica) {
      if (!usState.trim()) {
        setError("州を選択してください");
        return;
      }
    } else {
      if (!prefecture || !JAPAN_PREFECTURES.includes(prefecture)) {
        setError("都道府県情報が正しくありません");
        return;
      }
      if (!city.trim()) {
        setError("市区町村を選択してください");
        return;
      }
    }

    if (!auth.currentUser) return;

    try {
      const userDocRef = doc(db, "users", auth.currentUser.uid);

      if (isAmerica) {
        const stateData = usStateOptions.find(
          (s) => s.locationKey === `US:${usState}`
        );

        await updateDoc(userDocRef, {
          country: "アメリカ",
          adminArea1: usState,
          usState: usState,
          lat: stateData?.lat ?? null,
          lng: stateData?.lng ?? null,
          locationKey: stateData?.locationKey || `US:${usState}`,
          geoSource: "usStates.json",
          locationLabel: `アメリカ ${usState}`,
        });
      } else {
        const cityData = cityOptions.find((c) =>
          typeof c === "string" ? c === city : c?.name === city
        );
        const cityName = typeof cityData === "string" ? cityData : cityData?.name || city.trim();
        const cityLat = typeof cityData === "object" && Number.isFinite(cityData?.lat) ? cityData.lat : null;
        const cityLng = typeof cityData === "object" && Number.isFinite(cityData?.lng) ? cityData.lng : null;
        const cityLocKey = typeof cityData === "object" && cityData?.locationKey
          ? cityData.locationKey
          : `JP:${prefecture}:${city.trim()}`;

        await updateDoc(userDocRef, {
          country: "日本",
          adminArea1: prefecture,
          city: cityName,
          lat: cityLat,
          lng: cityLng,
          locationKey: cityLocKey,
          geoSource: "japanCities.json",
          locationLabel: `${prefecture} ${cityName}`,
        });
      }

      setError("");
      setOpenDialog(false);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("city update error:", err);
      setError("保存に失敗しました。時間をおいてもう一度お試しください。");
    }
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            px: { xs: 1, sm: 2 },
            py: 2,
            bgcolor: theme.surfaceBg,
            border: `1px solid ${theme.bottomBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.railActive,
            fontWeight: 700,
            textAlign: "center",
            fontSize: { xs: 22, sm: 26 },
            pb: 1,
          }}
        >
          {isAmerica ? "州のご登録をお願いします" : "市区町村のご登録をお願いします"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
            <TextField
              fullWidth
              label={isAmerica ? "居住地" : "都道府県"}
              value={prefecture}
              disabled
              sx={{ mb: 2 }}
            />

            {isAmerica ? (
              <>
                <Autocomplete
                  fullWidth
                  options={usStateOptions}
                  value={selectedUsState}
                  onChange={(_, newValue) => {
                    setUsState(
                      newValue
                        ? newValue.locationKey.replace("US:", "")
                        : ""
                    );
                    setError("");
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.locationKey === value?.locationKey
                  }
                  autoHighlight
                  clearOnBlur={false}
                  loading={loadingUsStates}
                  noOptionsText={
                    loadingUsStates
                      ? "読み込み中です…"
                      : "該当する州が見つかりません"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="州（State）"
                      placeholder="州名を入力して検索"
                      error={Boolean(error)}
                      helperText={error}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingUsStates ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1.5,
                    color: "rgba(0,0,0,0.56)",
                    lineHeight: 1.7,
                  }}
                >
                  ※お住まいの州を選択してください。
                </Typography>
              </>
            ) : (
              <>
                <Autocomplete
                  fullWidth
                  options={cityOptions}
                  value={selectedCity}
                  onChange={(_, newValue) => {
                    const name = typeof newValue === "string" ? newValue : newValue?.name || "";
                    setCity(name);
                    setError("");
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.name || ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    const a = typeof option === "string" ? option : option?.name || "";
                    const b = typeof value === "string" ? value : value?.name || "";
                    return a === b;
                  }}
                  filterOptions={filterOptions}
                  autoHighlight
                  clearOnBlur={false}
                  loading={loadingCities}
                  noOptionsText={
                    loadingCities
                      ? "読み込み中です…"
                      : "該当する市区町村が見つかりません"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="市区町村"
                      placeholder="市区町村を入力して検索"
                      error={Boolean(error)}
                      helperText={error}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCities ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1.5,
                    color: "rgba(0,0,0,0.56)",
                    lineHeight: 1.7,
                  }}
                >
                  ※市・区・町・村を含む一覧から選択してください。
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pt: 1, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              minWidth: 180,
              height: 48,
              borderRadius: 999,
              fontWeight: 700,
              bgcolor: theme.railActive,
              "&:hover": {
                bgcolor: theme.railActive,
                opacity: 0.92,
              },
            }}
          >
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          登録が完了しました。
        </Alert>
      </Snackbar>
    </>
  );
}